import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import logo from "../images/logo.png";
import BlockchainBackground from "../Components/BlockchainBackground";
import "../styles/BlockchainBackground.css";
import {useNavigate} from "react-router-dom";
import {useAuth} from "@clerk/clerk-react";

function LogInPage() {
    // Typing animation for "Welcome to BlockBallot"
    const [displayText, setDisplayText] = useState("");
    const [showCursor, setShowCursor] = useState(true);
    const fullText = "The Future of Voting.";
    const typingRef = useRef(null);
    const cursorRef = useRef(null);
    const navigate = useNavigate()
    const {isSignedIn} = useAuth()

    // Handle typing animation
    useEffect(() => {
        let currentIndex = 0;
        let mounted = true;

        // Clear any existing intervals to prevent issues
        if (typingRef.current) clearInterval(typingRef.current);
        
        // Reset text if component remounts
        setDisplayText("");
        
        // Create new typing interval
        typingRef.current = setInterval(() => {
            if (!mounted) return;
            
            if (currentIndex < fullText.length) {
                setDisplayText(fullText.substring(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typingRef.current);
            }
        }, 100);

        // Cursor blinking effect
        cursorRef.current = setInterval(() => {
            if (!mounted) return;
            setShowCursor(prev => !prev);
        }, 530);

        return () => {
            mounted = false;
            clearInterval(typingRef.current);
            clearInterval(cursorRef.current);
        };
    }, []);

    const handleAdminLogin = () =>{
        if (isSignedIn){
            navigate('/AdminDashboard')
        }else{
            navigate('/SignIn');
        }
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bgColor text-center font-[Montserrat] relative overflow-hidden">
            {/* BlockchainBackground as full screen background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <BlockchainBackground />
            </div>
            
            {/* Glass morphism card container */}
            <div className="relative z-10 flex flex-col items-center bg-[#154c79] bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-white border-opacity-20 max-w-md w-full mx-4">
                {/* Logo centered at the top */}
                <div className="relative mb-6">
                    <img 
                        src={logo} 
                        alt="BlockBallot Logo" 
                        className="h-24 mx-auto logo-enhanced hover:scale-105 transition-all duration-500"
                    />
                </div>
                
                {/* Typing effect for "Welcome to BlockBallot" */}
                <div className="h-8 mb-8 flex justify-center items-center">
                    <div className="text-xl font-semibold text-blue-950 flex items-center">
                        <span>{displayText}</span>
                        <span 
                            className={`h-5 border-r-2 border-blue-950 ml-1 ${showCursor ? 'opacity-100' : 'opacity-0'}`} 
                            style={{transitionProperty: 'opacity', transitionDuration: '0.1s'}}
                        ></span>
                    </div>
                </div>
                
                {/* Buttons with OR divider */}
                <div className="flex flex-col items-center space-y-6 w-full">
                    {/* Admin Login Button */}
                    <button
                        onClick={handleAdminLogin}
                        className="relative overflow-hidden w-full block text-center px-7 py-4 text-lg font-medium text-[#154c79] bg-white rounded-full transition-all duration-300 ease-in-out group hover:translate-y-[-3px] hover:shadow-lg shadow-md"
                    >
                        <span className="relative z-10 transition-colors duration-300 ease-in-out group-hover:text-white flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 group-hover:text-white text-[#154c79]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Admin Login
                        </span>
                        <span className="absolute inset-0 bg-[#154c79] scale-x-0 origin-left transition-transform duration-500 ease-in-out group-hover:scale-x-100"></span>
                    </button>

                    {/* OR Divider */}
                    <div className="flex items-center w-full my-2">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="mx-4 text-blue-900 text-sm font-medium">OR</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                    </div>
                    
                    {/* Voter Login Button */}
                    <Link
                        to={'/QRScanPage'}
                        className="relative overflow-hidden w-full block text-center px-7 py-4 text-lg font-medium text-[#154c79] bg-white rounded-full transition-all duration-300 ease-in-out group hover:translate-y-[-3px] hover:shadow-lg shadow-md"
                    >
                        <span className="relative z-10 transition-colors duration-300 ease-in-out group-hover:text-white flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 group-hover:text-white text-[#154c79]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                            </svg>
                            Voter Login
                        </span>
                        <span className="absolute inset-0 bg-[#154c79] scale-x-0 origin-left transition-transform duration-500 ease-in-out group-hover:scale-x-100"></span>
                    </Link>
                </div>
            </div>
            
            {/* Footer */}
            <footer className="absolute bottom-4 left-0 w-full text-gray-600 text-sm text-center z-10">
                &copy; BlockBallot. All Rights Reserved.
            </footer>
        </div>
    );
};

export default LogInPage;